import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Check, Crown, Sparkles, Circle, Loader2, Zap, 
  FileText, Target, Infinity, Brain, Shield, Rocket, Cpu,
  AlertCircle, RefreshCcw, Settings
} from 'lucide-react';
import { trackViewContent, trackInitiateCheckout } from '../lib/metaPixel';

// Fallback plans in case API fails - Only AI plans (FREE and PRO removed)
// Prices updated: €99.99 AI, €119.99 AI_UNLIMITED (+ IVA)
const FALLBACK_PLANS = [
  { name: "AI", price: 9999, priceId: "price_1TFr39BqzCEPBrFMtO49QQza", ai_limit: 50 },
  { name: "AI_UNLIMITED", price: 11999, priceId: "price_1THexiBqzCEPBrFMaoj9hymo", ai_limit: -1 }
];

// Plan features - Using translation keys
const getPlanFeatures = (t) => ({
  AI: [
    { icon: Infinity, text: t('markers_unlimited') },
    { icon: Brain, text: t('ai_view_detection') },
    { icon: Sparkles, text: t('ai_credits_50') },
    { icon: Cpu, text: t('credits_per_view') },
    { icon: Zap, text: t('manual_clicks_free') },
    { icon: FileText, text: t('full_export') },
    { icon: Shield, text: t('smartrevision_basic') }
  ],
  AI_UNLIMITED: [
    { icon: Infinity, text: t('everything_in_ai') },
    { icon: Rocket, text: t('unlimited_ai_credits') },
    { icon: Cpu, text: t('unlimited_view_scans') },
    { icon: Shield, text: t('priority_support') },
    { icon: Brain, text: t('smartrevision_unlimited') }
  ]
});

// Plan descriptions - Using translation keys
const getPlanDescriptions = (t) => ({
  AI: t('ai_plan_desc'),
  AI_UNLIMITED: t('ai_unlimited_desc')
});

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgrading, setUpgrading] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    loadPlans();
    // Track ViewContent for pricing page (Meta Pixel)
    trackViewContent({
      content_name: 'SmartBalloon Pricing',
      content_category: 'Subscription',
      content_type: 'pricing_page',
    });
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('[Subscription] Fetching plans from API...');
      const response = await subscriptionAPI.getPlans();
      console.log('[Subscription] API Response:', response.data);
      
      // API returns array directly: [{name, price, priceId}, ...]
      if (Array.isArray(response.data) && response.data.length > 0) {
        setPlans(response.data);
        console.log('[Subscription] Plans loaded:', response.data);
      } else {
        console.warn('[Subscription] Empty response, using fallback');
        setPlans(FALLBACK_PLANS);
      }
    } catch (err) {
      console.error('[Subscription] API Error:', err);
      setError('Failed to load plans. Using cached data.');
      setPlans(FALLBACK_PLANS);
      toast.error('Could not load plans from server');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planName) => {
    if (planName === user?.plan?.toUpperCase()) return;
    
    // Track InitiateCheckout event (Meta Pixel)
    const plan = plans.find(p => p.name === planName);
    trackInitiateCheckout({
      content_name: planName,
      content_type: 'subscription',
      value: plan ? plan.price / 100 : 0,
      currency: 'EUR',
    });
    
    setUpgrading(planName);
    try {
      console.log('[Subscription] Starting checkout for:', planName);
      const response = await subscriptionAPI.checkout(planName, window.location.origin);
      console.log('[Subscription] Checkout response:', response.data);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('[Subscription] Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Errore durante il checkout');
      setUpgrading(null);
    }
  };

  const handleManageSubscription = async () => {
    setOpeningPortal(true);
    try {
      const response = await subscriptionAPI.customerPortal();
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (err) {
      console.error('[Subscription] Customer portal error:', err);
      toast.error(err.response?.data?.detail || t('error'));
      setOpeningPortal(false);
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName) {
      case 'AI_UNLIMITED':
        return (
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-200">
            <Rocket className="w-8 h-8 text-white" />
          </div>
        );
      case 'AI':
        return (
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-200">
            <Brain className="w-8 h-8 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200">
            <Brain className="w-8 h-8 text-white" />
          </div>
        );
    }
  };

  const getPlanStyle = (planName) => {
    const isCurrentPlan = planName?.toUpperCase() === user?.plan?.toUpperCase();
    
    if (isCurrentPlan) {
      return 'border-2 border-primary ring-4 ring-primary/20 bg-primary/5';
    }
    if (planName === 'AI_UNLIMITED') {
      return 'border-2 border-purple-400 hover:border-purple-500 hover:shadow-xl hover:shadow-purple-100 transition-all bg-gradient-to-b from-purple-50/50 to-white';
    }
    if (planName === 'AI') {
      return 'border-2 border-amber-300 hover:border-amber-400 hover:shadow-xl hover:shadow-amber-100 transition-all';
    }
    return 'border-2 border-slate-200 hover:border-slate-300 transition-all';
  };

  const formatPrice = (cents) => {
    if (cents === 0) return t('free_plan');
    return `€${(cents / 100).toFixed(2)}`;
  };

  const planRanks = { 'AI_TRIAL': 0, 'AI': 1, 'AI_UNLIMITED': 2 };
  
  const getButtonConfig = (planName) => {
    const currentPlan = user?.plan?.toUpperCase() || 'AI_TRIAL';
    const currentRank = planRanks[currentPlan] || 0;
    const targetRank = planRanks[planName] || 0;
    
    // Trial users always see "Upgrade" for all paid plans
    if (currentPlan === 'AI_TRIAL') {
      return { text: t('upgrade_now'), variant: 'default', disabled: false };
    }
    
    if (planName?.toUpperCase() === currentPlan) {
      return { text: t('current_plan'), variant: 'outline', disabled: true };
    }
    if (targetRank > currentRank) {
      return { text: t('upgrade'), variant: 'default', disabled: false };
    }
    return { text: t('change_plan'), variant: 'outline', disabled: false };
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="h-14 border-b border-slate-200 bg-white/80 backdrop-blur-sm flex items-center px-6 sticky top-0 z-10">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('dashboard')}
        </Button>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto p-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-slate-900 mb-3">
            {t('choose_your_plan')}
          </h1>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            {t('unlock_potential')}
          </p>
          
          {/* Trial Status Banner - Prominente con countdown */}
          {user?.plan === 'AI_TRIAL' && (
            <div className="mt-6 max-w-2xl mx-auto">
              <div className="p-6 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl text-white shadow-lg shadow-orange-200">
                <div className="flex items-center justify-center gap-4">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    {user?.trial_end_date && (() => {
                      const trialEnd = new Date(user.trial_end_date);
                      const now = new Date();
                      const daysLeft = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
                      return (
                        <span className="text-3xl font-black">{daysLeft}</span>
                      );
                    })()}
                  </div>
                  <div className="text-left">
                    <p className="text-2xl font-bold flex items-center gap-2">
                      <Sparkles className="w-6 h-6" />
                      {t('trial_plan')}
                    </p>
                    <p className="text-lg opacity-90">
                      {user?.trial_end_date && (() => {
                        const trialEnd = new Date(user.trial_end_date);
                        const now = new Date();
                        const daysLeft = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
                        if (daysLeft <= 0) return t('trial_expired');
                        if (daysLeft === 1) return `1 ${t('trial_day_remaining')}`;
                        return `${daysLeft} ${t('trial_days_remaining')}`;
                      })()}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/30 text-sm opacity-90 text-center">
                  <p>{t('trial_includes')}:</p>
                  <p className="mt-1">• {t('trial_smartballoon_limit')} • {t('trial_smartrevision_limit')}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Manage Subscription Button - Only for AI users with active subscription */}
          {(user?.plan === 'AI' || user?.plan === 'AI_UNLIMITED') && (
            <>
              {/* Subscription Cancelled Banner - Same style as Trial */}
              {user?.subscription_end_date && (
                <div className="mt-6 max-w-2xl mx-auto">
                  <div className="p-6 bg-gradient-to-r from-red-500 via-rose-500 to-pink-500 rounded-2xl text-white shadow-lg shadow-red-200">
                    <div className="flex items-center justify-center gap-4">
                      <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                        {(() => {
                          const endDate = new Date(user.subscription_end_date);
                          const now = new Date();
                          const daysLeft = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
                          return (
                            <span className="text-3xl font-black">{daysLeft}</span>
                          );
                        })()}
                      </div>
                      <div className="text-left">
                        <p className="text-2xl font-bold flex items-center gap-2">
                          <AlertCircle className="w-6 h-6" />
                          {t('subscription_cancelled')}
                        </p>
                        <p className="text-lg opacity-90">
                          {(() => {
                            const endDate = new Date(user.subscription_end_date);
                            const now = new Date();
                            const daysLeft = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
                            if (daysLeft <= 0) return t('subscription_expires_today');
                            if (daysLeft === 1) return `1 ${t('subscription_day_remaining')}`;
                            return `${daysLeft} ${t('subscription_days_remaining')}`;
                          })()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/30 text-sm opacity-90 text-center">
                      <p>{t('subscription_access_until')} <strong>{new Date(user.subscription_end_date).toLocaleDateString()}</strong></p>
                      <p className="mt-1 text-white/70">{t('subscription_cancelled_hint')}</p>
                    </div>
                    <div className="mt-4 text-center">
                      <Button
                        variant="secondary"
                        onClick={handleManageSubscription}
                        disabled={openingPortal}
                        className="bg-white text-rose-600 hover:bg-white/90 font-semibold"
                      >
                        {openingPortal ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            {t('loading')}
                          </>
                        ) : (
                          <>
                            <RefreshCcw className="w-4 h-4 mr-2" />
                            {t('subscription_reactivate')}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Normal Manage Subscription Button (when not cancelled) */}
              {!user?.subscription_end_date && (
                <div className="mt-6">
                  <Button
                    variant="outline"
                    onClick={handleManageSubscription}
                    disabled={openingPortal}
                    className="gap-2"
                    data-testid="manage-subscription-btn"
                  >
                    {openingPortal ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('loading')}
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4" />
                        {t('manage_subscription') || 'Manage Subscription'}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">
                    {t('manage_subscription_desc') || 'Cancel, update payment, download invoices'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-center">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <p className="text-slate-500">{t('loading_plans')}</p>
          </div>
        ) : (
          /* Plans Grid - Centered with larger cards */
          <div className="flex justify-center gap-8">
            {plans.map((plan) => {
              const PLAN_FEATURES = getPlanFeatures(t);
              const PLAN_DESCRIPTIONS = getPlanDescriptions(t);
              const features = PLAN_FEATURES[plan.name] || [];
              const buttonConfig = getButtonConfig(plan.name);
              const description = PLAN_DESCRIPTIONS[plan.name] || '';
              
              return (
                <Card 
                  key={plan.name}
                  className={`relative overflow-hidden w-full max-w-md ${getPlanStyle(plan.name)}`}
                  data-testid={`plan-card-${plan.name}`}
                >
                  {/* Popular Badge for AI */}
                  {plan.name === 'AI' && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg">
                        {t('popular')}
                      </div>
                    </div>
                  )}
                  
                  {/* Best Value Badge for AI_UNLIMITED */}
                  {plan.name === 'AI_UNLIMITED' && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-gradient-to-r from-purple-500 to-violet-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg">
                        {t('best_value')}
                      </div>
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {plan.name?.toUpperCase() === user?.plan?.toUpperCase() && (
                    <div className="absolute -top-px left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1.5 bg-primary text-white text-xs font-bold rounded-b-lg shadow-lg">
                        {t('your_plan')}
                      </span>
                    </div>
                  )}

                  <CardHeader className="text-center pt-10 pb-6">
                    <div className="flex justify-center mb-5">
                      {getPlanIcon(plan.name)}
                    </div>
                    <CardTitle className="text-2xl font-bold">{plan.name.replace('_', ' ')}</CardTitle>
                    {description && (
                      <p className="text-sm text-slate-500 mt-2">{description}</p>
                    )}
                    <CardDescription className="mt-4">
                      <span className="text-4xl font-black text-slate-900">
                        {formatPrice(plan.price)}
                      </span>
                      {plan.price > 0 && (
                        <>
                          <span className="text-slate-500 ml-1 text-base">/{t('monthly')}</span>
                          <span className="block text-xs text-slate-400 mt-1">{t('plus_vat')}</span>
                        </>
                      )}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6 pb-10">
                    {/* Features List */}
                    <ul className="space-y-3">
                      {features.map((feature, idx) => {
                        const IconComponent = feature.icon;
                        return (
                          <li key={idx} className="flex items-center gap-3 text-sm">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              plan.name === 'AI_UNLIMITED' ? 'bg-purple-100 text-purple-600' :
                              plan.name === 'AI' ? 'bg-amber-100 text-amber-600' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <span className="text-slate-700">{feature.text}</span>
                          </li>
                        );
                      })}
                    </ul>

                    {/* CTA Button */}
                    <Button
                      className={`w-full h-14 text-lg font-semibold ${
                        plan.name === 'AI' && !buttonConfig.disabled
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-amber-200' 
                          : plan.name === 'AI_UNLIMITED' && !buttonConfig.disabled
                            ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-lg shadow-purple-200'
                            : ''
                      }`}
                      variant={buttonConfig.variant}
                      disabled={buttonConfig.disabled || upgrading !== null}
                      onClick={() => handleUpgrade(plan.name)}
                      data-testid={`upgrade-${plan.name}`}
                    >
                      {upgrading === plan.name && (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      )}
                      {buttonConfig.text}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && plans.length === 0 && (
          <div className="text-center py-20">
            <Circle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2">No plans available</h3>
            <p className="text-slate-500 mb-4">Please try again later</p>
            <Button onClick={loadPlans}>Retry</Button>
          </div>
        )}

        {/* Trust Badges */}
        <div className="mt-16 flex flex-wrap justify-center gap-8 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-500" />
            <span>Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-500" />
            <span>Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-green-500" />
            <span>Instant Activation</span>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-12 text-center text-sm text-slate-500">
          <p>All plans include secure cloud storage for your projects.</p>
          <p className="mt-1">Need help? Contact <a href="mailto:info@lscsrl.it" className="text-primary hover:underline">info@lscsrl.it</a></p>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
